/**
 * Copilot Actions — the confirmation card (iter338).
 *
 * Copilot proposes; this is where the manager's finger fires it. The card spells out exactly
 * what will happen (including anything that texts or emails a real person), and its OUTCOME is
 * written back onto the chat message — so a refresh shows "done", never a second live button.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, RotateCcw, AlertTriangle, X, Zap } from "lucide-react";

const UNDO_WINDOW = 60;

export const CopilotActionCard = ({ tenantId, action, result, dismissed, onPatch }) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [undoLeft, setUndoLeft] = useState(0);
  const [undone, setUndone] = useState(false);

  useEffect(() => {
    if (!result?.undo_token || result.undone) return;
    const started = result.at ? (Date.now() - new Date(result.at).getTime()) / 1000 : 0;
    const left = Math.max(0, Math.round(UNDO_WINDOW - started));
    setUndoLeft(left);
    if (!left) return;
    const id = setInterval(() => setUndoLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [result?.undo_token, result?.at, result?.undone]);

  const run = async () => {
    setRunning(true);
    setError("");
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/actions/run`, {
        name: action.name, args: action.args,
      });
      onPatch({ action_result: { ...r.data, at: new Date().toISOString() } });
      toast.success(r.data.message);
    } catch (e) {
      setError(e.response?.data?.detail || "That didn't go through.");
    } finally {
      setRunning(false);
    }
  };

  const undo = async () => {
    try {
      await api.post(`/tenants/${tenantId}/copilot/actions/undo`, { token: result.undo_token });
      setUndone(true);
      onPatch({ action_result: { ...result, undone: true } });
      toast.success("Reverted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Too late to undo that one.");
      setUndoLeft(0);
    }
  };

  if (dismissed) {
    return (
      <div className="mt-2 text-[11px] text-ink/40 flex items-center gap-1" data-testid="copilot-action-dismissed">
        <X className="h-3 w-3" /> Didn't run it.
      </div>
    );
  }

  if (result) {
    return (
      <div className="mt-2 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-2.5"
           data-testid="copilot-action-done">
        <div className="flex items-start gap-2">
          <Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">{result.message}</div>
            {(undone || result.undone) && (
              <div className="text-[11px] text-ink/50 mt-0.5">Reverted.</div>
            )}
          </div>
          {result.undo_token && !result.undone && !undone && undoLeft > 0 && (
            <button onClick={undo} data-testid="copilot-action-undo"
              className="shrink-0 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink hover:bg-sand-50 transition-colors flex items-center gap-1.5">
              <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
              {result.undo_label || "Undo"} · {undoLeft}s
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-2xl border border-ink/15 bg-sand-50 px-3.5 py-3" data-testid="copilot-action-card">
      <div className="flex items-center gap-2">
        <Zap className="h-3.5 w-3.5 text-redline-500 shrink-0" strokeWidth={2.5} />
        <div className="text-sm font-semibold text-ink min-w-0" data-testid="copilot-action-title">
          {action.title}
        </div>
      </div>
      {action.lines?.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 pl-5" data-testid="copilot-action-lines">
          {action.lines.map((l, i) => (
            <li key={i} className="text-xs text-ink/70 list-disc list-outside">{l}</li>
          ))}
        </ul>
      )}
      {action.warning && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700" data-testid="copilot-action-warning">
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" strokeWidth={2.5} /> {action.warning}
        </div>
      )}
      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-600" data-testid="copilot-action-error">
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" strokeWidth={2.5} /> {error}
        </div>
      )}
      <div className="mt-2.5 flex items-center gap-2">
        <button onClick={run} disabled={running} data-testid="copilot-action-confirm"
          className="rounded-full bg-ink text-white text-xs font-semibold px-4 py-2 hover:bg-ink-700 transition-colors disabled:opacity-50 flex items-center gap-1.5">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
          {running ? "Working…" : (action.confirm_label || "Confirm")}
        </button>
        <button onClick={() => onPatch({ action_dismissed: true })} disabled={running}
          data-testid="copilot-action-cancel"
          className="rounded-full border border-sand-200 bg-white text-xs font-semibold px-3.5 py-2 text-ink/70 hover:bg-sand-50 transition-colors disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
};
